import { AlertTriangle, Check } from "lucide-react";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";
import type { StateTone } from "@/shared/utils/labels";

/**
 * `check` arrived with the 2026-09 redesign: granted stopped being a tinted chip and became a tick
 * and a word, so the ordinary case still carries a SHAPE and never relies on colour alone — which is
 * the rule that made dropping the tint safe in the first place.
 */
export type DotShape = "filled" | "hollow" | "alert" | "check";

export const TONE_TEXT: Record<StateTone, string> = {
  success: "text-success",
  danger: "text-danger",
  warning: "text-warning",
  neutral: "text-neutral-state",
  accent: "text-accent",
  info: "text-info",
};

interface StatusDotProps {
  tone: StateTone;
  /** filled = online, hollow = offline/unknown, alert = needs attention, check = granted. */
  shape?: DotShape;
  /**
   * Set when the dot sits on a solid fill, where the tone's own ink would vanish into it. Only the
   * denied chip does this, and only because solid `--danger` is reserved for exactly that.
   */
  onSolid?: boolean;
  /** 1.2 s pulse — only for "live" (design-system §5). */
  live?: boolean;
  className?: string;
}

/** Purely visual; always paired with words by StatusChip or a label next to it (§6). */
export function StatusDot({
  tone,
  shape = "filled",
  live = false,
  onSolid = false,
  className,
}: StatusDotProps) {
  const ink = onSolid ? "text-on-accent" : TONE_TEXT[tone];
  if (shape === "alert") return <Icon icon={AlertTriangle} size={16} className={cn(ink, className)} />;
  if (shape === "check") return <Icon icon={Check} size={16} className={cn("size-3.5", ink, className)} />;
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-(--dot-size) shrink-0 rounded-full",
        ink,
        shape === "filled" ? "bg-current" : "border-(length:--dot-border) border-current bg-transparent",
        live && "animate-live",
        className,
      )}
    />
  );
}
